import type { ComponentChildren, JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import type { QueueStatus } from "@/api/types";

export type SwipeAction = "archive" | "keep" | "skip" | "review" | "deep_read";

const DRAG_THRESHOLD = 90;
const WHEEL_THRESHOLD = 240;
const WHEEL_IDLE_MS = 150;
const WHEEL_COOLDOWN_MS = 600;

const FLY: Record<SwipeAction, [string, string, number]> = {
  archive: ["-120vw", "0", -18],
  review: ["120vw", "0", 18],
  deep_read: ["120vw", "0", 18],
  keep: ["0", "-130vh", 0],
  skip: ["0", "130vh", 6],
};

let lastWheelCommit = 0;

export function swipeActionForOffset(
  status: QueueStatus,
  dx: number,
  dy: number,
  threshold = DRAG_THRESHOLD,
): SwipeAction | null {
  if (status !== "new" && status !== "review") return null;
  const horizontal = Math.abs(dx) >= Math.abs(dy);
  if (horizontal) {
    if (dx > threshold) return status === "new" ? "review" : "deep_read";
    if (dx < -threshold) return "archive";
    return null;
  }
  if (dy < -threshold) return "keep";
  if (dy > threshold) return "skip";
  return null;
}

function previewClass(action: SwipeAction | null): string {
  if (action === "archive") return "will-archive";
  if (action === "keep") return "will-keep";
  if (action === "skip") return "will-skip";
  if (action === "review" || action === "deep_read") return "will-right";
  return "";
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest("a, button, input, select, textarea, summary, audio, dialog") !== null;
}

export function SwipeDeck({
  status,
  busy,
  onAction,
  children,
}: {
  status: "new" | "review";
  busy: boolean;
  onAction: (action: SwipeAction) => boolean | Promise<boolean>;
  children: (commit: (action: SwipeAction) => void) => ComponentChildren;
}) {
  const surface = useRef<HTMLDivElement>(null);
  const done = useRef(false);
  const wheelX = useRef(0);
  const wheelTimer = useRef<number | null>(null);
  const [intent, setIntent] = useState<SwipeAction | null>(null);
  const [style, setStyle] = useState<JSX.CSSProperties>({});
  const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

  const reset = (): void => {
    done.current = false;
    setIntent(null);
    setStyle({ transition: reduceMotion ? "none" : "transform .2s ease" });
  };

  const preview = (dx: number, dy: number): void => {
    const horizontal = Math.abs(dx) >= Math.abs(dy);
    setIntent(swipeActionForOffset(status, dx, dy));
    setStyle({
      transition: "none",
      transform: reduceMotion
        ? undefined
        : horizontal
          ? `translateX(${dx}px) rotate(${dx / 24}deg)`
          : `translateY(${dy}px)`,
    });
  };

  const commit = (action: SwipeAction): void => {
    if (done.current || busy) return;
    done.current = true;
    setIntent(action);
    const [x, y, rotation] = FLY[action];
    setStyle(reduceMotion ? { transition: "none" } : {
      transition: "transform .3s ease, opacity .3s ease",
      transform: `translate(${x}, ${y}) rotate(${rotation}deg)`,
      opacity: 0,
    });
    const run = (): void => {
      void Promise.resolve(onAction(action)).then((accepted) => {
        if (!accepted) reset();
      });
    };
    if (reduceMotion) run();
    else window.setTimeout(run, 180);
  };

  useEffect(() => {
    const card = surface.current;
    if (!card) return;

    let startX = 0;
    let startY = 0;
    let dx = 0;
    let touching = false;
    let horizontal: boolean | null = null;
    let drag: { x: number; y: number; pointerId: number } | null = null;

    const onTouchStart = (event: TouchEvent): void => {
      if (busy || done.current || isInteractiveTarget(event.target) || !event.touches[0]) return;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      dx = 0;
      touching = true;
      horizontal = null;
    };
    const onTouchMove = (event: TouchEvent): void => {
      if (!touching || !event.touches[0]) return;
      dx = event.touches[0].clientX - startX;
      const dy = event.touches[0].clientY - startY;
      if (horizontal === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) horizontal = Math.abs(dx) > Math.abs(dy);
      if (horizontal) preview(dx, 0);
    };
    const onTouchEnd = (): void => {
      if (!touching) return;
      touching = false;
      const action = horizontal ? swipeActionForOffset(status, dx, 0) : null;
      if (action) commit(action);
      else reset();
    };
    const onMouseDown = (event: MouseEvent): void => {
      if (event.button === 1) event.preventDefault();
    };
    const onPointerDown = (event: PointerEvent): void => {
      if (event.button !== 1 || busy || done.current || isInteractiveTarget(event.target)) return;
      event.preventDefault();
      card.setPointerCapture(event.pointerId);
      drag = { x: event.clientX, y: event.clientY, pointerId: event.pointerId };
    };
    const onPointerMove = (event: PointerEvent): void => {
      if (drag) preview(event.clientX - drag.x, event.clientY - drag.y);
    };
    const finishPointer = (event: PointerEvent): void => {
      if (!drag) return;
      const action = swipeActionForOffset(status, event.clientX - drag.x, event.clientY - drag.y);
      if (card.hasPointerCapture(drag.pointerId)) card.releasePointerCapture(drag.pointerId);
      drag = null;
      if (action) commit(action);
      else reset();
    };
    const cancelPointer = (): void => {
      if (!drag) return;
      if (card.hasPointerCapture(drag.pointerId)) card.releasePointerCapture(drag.pointerId);
      drag = null;
      reset();
    };
    const onAuxClick = (event: MouseEvent): void => {
      if (event.button === 1) event.preventDefault();
    };
    const clearWheel = (): void => {
      wheelX.current = 0;
      if (!done.current) reset();
    };
    const onWheel = (event: WheelEvent): void => {
      if (busy) return;
      if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) return;
      event.preventDefault();
      if (done.current || Date.now() - lastWheelCommit < WHEEL_COOLDOWN_MS) return;
      if (wheelTimer.current !== null) window.clearTimeout(wheelTimer.current);
      wheelTimer.current = window.setTimeout(clearWheel, WHEEL_IDLE_MS);
      wheelX.current += event.deltaX;
      const offset = wheelX.current;
      preview(offset * 0.5, 0);
      const action = swipeActionForOffset(status, offset, 0, WHEEL_THRESHOLD);
      if (action) {
        lastWheelCommit = Date.now();
        commit(action);
      }
    };

    card.addEventListener("touchstart", onTouchStart, { passive: true });
    card.addEventListener("touchmove", onTouchMove, { passive: true });
    card.addEventListener("touchend", onTouchEnd);
    card.addEventListener("mousedown", onMouseDown);
    card.addEventListener("pointerdown", onPointerDown);
    card.addEventListener("pointermove", onPointerMove);
    card.addEventListener("pointerup", finishPointer);
    card.addEventListener("pointercancel", cancelPointer);
    card.addEventListener("auxclick", onAuxClick);
    card.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      if (wheelTimer.current !== null) window.clearTimeout(wheelTimer.current);
      card.removeEventListener("touchstart", onTouchStart);
      card.removeEventListener("touchmove", onTouchMove);
      card.removeEventListener("touchend", onTouchEnd);
      card.removeEventListener("mousedown", onMouseDown);
      card.removeEventListener("pointerdown", onPointerDown);
      card.removeEventListener("pointermove", onPointerMove);
      card.removeEventListener("pointerup", finishPointer);
      card.removeEventListener("pointercancel", cancelPointer);
      card.removeEventListener("auxclick", onAuxClick);
      card.removeEventListener("wheel", onWheel);
    };
  }, [busy, status]);

  const rightLabel = status === "new" ? "复筛" : "精读";
  return (
    <div
      ref={surface}
      class={`swipe-card swipe-stage-${status} ${previewClass(intent)}`.trim()}
      style={style}
      data-testid="swipe-card"
    >
      <div class="swipe-hint swipe-hint-left" aria-hidden="true">归档</div>
      <div class="swipe-hint swipe-hint-right" aria-hidden="true">{rightLabel}</div>
      <div class="swipe-hint swipe-hint-up" aria-hidden="true">保留</div>
      <div class="swipe-hint swipe-hint-down" aria-hidden="true">下一个</div>
      {children(commit)}
    </div>
  );
}
